import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import Button from '@components/common/Button';
import { Badge } from '@components/common/Badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/common/Dialog';
import { Input } from '@components/common/Input';
import { useGrants, useAddGrant, useDeleteGrant } from '../../../api/hooks/useDashboards';
import { useListRoles } from '@api/hooks/useRoles';
import { useListUsers } from '@api/hooks/useUsers';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import type { GrantResponse } from '../../../types/analytics';

interface Props { dashboardId: string; onClose: () => void; }

type TargetType = 'role' | 'user';
const ACCESS_LEVEL_OPTIONS = [
  { id: 'view', label: 'View' },
  { id: 'edit', label: 'Edit' },
];

export function ShareDialog({ dashboardId, onClose }: Props) {
  const { data: grants } = useGrants(dashboardId);
  const { data: rolesResp } = useListRoles(0, 100);
  const { data: usersResp } = useListUsers(0, 100);
  const roles = rolesResp?.items ?? [];
  const roleById = useMemo(() => new Map(roles.map((r) => [r.id, r])), [roles]);
  const userById = useMemo(
    () => new Map((usersResp?.items ?? []).map((u) => [u.id, u])), [usersResp]);

  const addGrant = useAddGrant();
  const deleteGrant = useDeleteGrant();

  const [targetType, setTargetType] = useState<TargetType>('role');
  const [level, setLevel] = useState<'view' | 'edit'>('view');
  const [roleId, setRoleId] = useState('');
  const [personSearch, setPersonSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserLabel, setSelectedUserLabel] = useState('');

  const debouncedSearch = useDebouncedValue(personSearch, 300);
  const { data: searchResp, isLoading: searchLoading } = useListUsers(0, 8, debouncedSearch);
  const searchResults = searchResp?.items ?? [];

  const selectPerson = (id: string, label: string) => {
    setSelectedUserId(id);
    setSelectedUserLabel(label);
    setPersonSearch('');
  };

  const clearPersonSelection = (value: string) => {
    setSelectedUserId('');
    setSelectedUserLabel('');
    setPersonSearch(value);
  };

  const handleAddRole = () => {
    if (!roleId) return;
    addGrant.mutate({ dashboardId, body: { grantee_type: 'role', grantee_id: roleId, access_level: level } });
    setRoleId('');
  };

  const handleAddPerson = () => {
    if (!selectedUserId) return;
    addGrant.mutate({
      dashboardId, body: { grantee_type: 'user', grantee_id: selectedUserId, access_level: level },
    });
    clearPersonSelection('');
  };

  const grantLabel = (g: GrantResponse) => {
    if (g.grantee_type === 'role') {
      const role = roleById.get(g.grantee_id);
      return role ? `Role: ${role.name}` : `Role: ${g.grantee_id.slice(0, 8)}…`;
    }
    const user = userById.get(g.grantee_id);
    return user ? `${user.first_name} ${user.last_name}` : `Person: ${g.grantee_id.slice(0, 8)}…`;
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }} size="lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share dashboard</DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 mb-3" role="group" aria-label="Share with">
          <Button
            type="button"
            size="sm"
            variant={targetType === 'role' ? 'primary' : 'secondary'}
            onClick={() => setTargetType('role')}
          >
            Role
          </Button>
          <Button
            type="button"
            size="sm"
            variant={targetType === 'user' ? 'primary' : 'secondary'}
            onClick={() => setTargetType('user')}
          >
            Person
          </Button>
        </div>

        {targetType === 'role' ? (
          <div className="flex gap-2 mb-4">
            <SearchableSelect
              className="flex-1"
              value={roleId}
              onChange={setRoleId}
              aria-label="Role"
              options={[{ id: '', label: 'Select role...' }, ...roles.map((role) => ({ id: role.id, label: role.name }))]}
              placeholder="Select role..."
              clearable={false}
            />
            <SearchableSelect
              className="w-28"
              value={level}
              onChange={(value) => setLevel(value as 'view' | 'edit')}
              aria-label="Access level"
              options={ACCESS_LEVEL_OPTIONS}
              placeholder="Access"
              clearable={false}
            />
            <Button size="sm" disabled={!roleId} onClick={handleAddRole}>Add</Button>
          </div>
        ) : (
          <div className="mb-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  aria-label="Search people"
                  placeholder="Search by name or email..."
                  value={selectedUserLabel || personSearch}
                  onChange={(e) => clearPersonSelection(e.target.value)}
                />
                {personSearch && !selectedUserId && (
                  <ul className="absolute z-10 mt-1 max-h-32 w-full overflow-auto rounded-lg border border-border-default bg-surface-base text-sm shadow-lg">
                    {searchLoading && <li className="px-2 py-1 text-content-muted">Searching...</li>}
                    {!searchLoading && searchResults.length === 0 && (
                      <li className="px-2 py-1 text-content-muted">No matches</li>
                    )}
                    {searchResults.map((u) => (
                      <li key={u.id}>
                        <button type="button"
                          className="w-full px-2 py-1 text-left text-content-primary transition-colors hover:bg-primary-50"
                          onClick={() => selectPerson(u.id, `${u.first_name} ${u.last_name}`)}>
                          {u.first_name} {u.last_name} <span className="text-content-muted">- {u.email}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <SearchableSelect
                className="w-28"
                value={level}
                onChange={(value) => setLevel(value as 'view' | 'edit')}
                aria-label="Access level"
                options={ACCESS_LEVEL_OPTIONS}
                placeholder="Access"
                clearable={false}
              />
              <Button size="sm" disabled={!selectedUserId} onClick={handleAddPerson}>Add</Button>
            </div>
          </div>
        )}

        <ul className="mb-4 max-h-40 overflow-auto text-sm">
          {(grants ?? []).map((g) => (
            <li key={g.id} className="flex items-center justify-between gap-2 border-b border-border-subtle py-2">
              <span className="text-content-primary">{grantLabel(g)}</span>
              <span className="flex items-center gap-2">
                <Badge>{g.access_level}</Badge>
                <Button
                  type="button"
                  aria-label={`Remove ${grantLabel(g)}`}
                  size="icon"
                  variant="ghost"
                  onClick={() => deleteGrant.mutate({ dashboardId, grantId: g.id })}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </span>
            </li>
          ))}
        </ul>
        <div className="flex justify-end"><Button variant="secondary" onClick={onClose}>Done</Button></div>
      </DialogContent>
    </Dialog>
  );
}
